/**
 * Shared student identity resolution.
 *
 * A single student can appear multiple times across sources: a Razorpay
 * website payment, a Google-Form/manual intake import, or a retried checkout.
 * Records are merged when ANY of these match:
 *   1. exact 10-digit mobile (country code trimmed)
 *   2. exact email (case-insensitive)
 *   3. exact full name (trimmed, whitespace-collapsed)
 */

export type IdentityFields = {
  email?: string | null;
  phone?: string | null;
  name?: string | null;
  fallbackId?: string | null;
};

export function normEmail(v: string | null | undefined): string {
  return (v ?? "").trim().toLowerCase();
}

/** Last 10 digits, ignoring country codes / separators. */
export function normPhone10(v: string | null | undefined): string {
  const d = (v ?? "").replace(/\D/g, "").slice(-10);
  if (d.length !== 10 || d === "0000000000") return "";
  return d;
}

export function normName(v: string | null | undefined): string {
  const n = (v ?? "").trim().replace(/\s+/g, " ").toLowerCase();
  return n === "unknown" || n === "student" ? "" : n;
}

/** Every identity key a record can be matched on. */
export function identityKeys(f: IdentityFields): string[] {
  const keys: string[] = [];
  const e = normEmail(f.email);
  if (e) keys.push(`e:${e}`);
  const p = normPhone10(f.phone);
  if (p) keys.push(`p:${p}`);
  const n = normName(f.name);
  if (n) keys.push(`n:${n}`);
  if (!keys.length) keys.push(`id:${f.fallbackId ?? Math.random().toString(36).slice(2)}`);
  return keys;
}

/**
 * Union-find grouping: returns clusters of records that share any identity key.
 */
export function groupByIdentity<T>(rows: T[], getFields: (row: T) => IdentityFields): T[][] {
  const parent = new Map<string, string>();
  const find = (k: string): string => {
    let root = parent.get(k) ?? k;
    if (root !== k) {
      root = find(root);
      parent.set(k, root);
    }
    return root;
  };
  const union = (a: string, b: string) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent.set(ra, rb);
  };

  const rowKeys = rows.map((r) => identityKeys(getFields(r)));
  rowKeys.forEach((keys) => {
    keys.forEach((k) => {
      if (!parent.has(k)) parent.set(k, k);
    });
    for (let i = 1; i < keys.length; i++) union(keys[0]!, keys[i]!);
  });

  const groups = new Map<string, T[]>();
  rows.forEach((row, i) => {
    const root = find(rowKeys[i]![0]!);
    const arr = groups.get(root);
    if (arr) arr.push(row);
    else groups.set(root, [row]);
  });
  return Array.from(groups.values());
}

/** Ordered unique list of every email seen in a merged cluster. */
export function collectEmails(values: Array<string | null | undefined>): string[] {
  const out: string[] = [];
  for (const v of values) {
    const e = normEmail(v);
    if (e && !out.includes(e)) out.push(e);
  }
  return out;
}
